<template>
  <b-modal
    modal-class="gr-select-po-modal"
    id="goodsReceiveSelectPOModal"
    ref="goodsReceiveSelectPOModal"
    title="รายการใบรับสินค้า"
    size="xl"
    centered
    no-close-on-backdrop
    @hide="close"
  >
    <!-- Form Section -->
    <div class="gr-form-section">
      <div class="gr-form-row">
        <span class="gr-label">รหัสใบสั่งซื้ออ้างอิง:</span>
        <div class="gr-field-wrapper">
          <!-- Create mode: dropdown -->
          <template v-if="!isEditMode">
            <MultipleSelectField
              :value="selectedPo"
              :options="poOptions"
              :is-required="true"
              :is-error="submitted && !selectedPoId"
              :show-error-icon="false"
              track-by="id"
              :custom-label="po => po.no"
              placeholder="เลือกรหัสใบสั่งซื้อ"
              select-label=""
              selected-label=""
              deselect-label=""
              :allow-empty="false"
              @input="selectedPoId = $event ? $event.id : null"
            />
          </template>
          <!-- Edit mode: read-only text -->
          <span
            v-else
            class="gr-info-text"
            >{{ editGrData ? editGrData.poRefCode : "—" }}</span
          >
        </div>
      </div>

      <div
        v-show="showContent"
        ref="fileSection"
        class="gr-form-row"
      >
        <span class="gr-label gr-label-top">ไฟล์ใบรับสินค้า:</span>
        <div class="gr-field-wrapper">
          <div
            class="gr-upload-box"
            @click="triggerFileInput"
          >
            <div class="gr-upload-text-area">
              <span class="gr-upload-placeholder">
                คลิกเพื่ออัปโหลด (เฉพาะไฟล์ PDF, JPG, JPEG, PNG ขนาดไม่เกิน 15
                MB ต่อไฟล์)
              </span>
            </div>
            <div class="gr-upload-action"><span>อัปโหลด</span></div>
            <input
              ref="fileInput"
              type="file"
              class="gr-file-input"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png,image/jpg"
              @change="onFileChange"
            />
          </div>
          <div
            v-if="existingFiles.length > 0 || uploadedFiles.length > 0"
            class="gr-file-chip-list"
          >
            <GrFileChip
              v-for="file in existingFiles"
              :key="'existing-' + file.id"
              :filename="file.originalName"
              @click="previewFile(file.id)"
              @remove="removeExistingFile(file.id)"
            />
            <GrFileChip
              v-for="(file, index) in uploadedFiles"
              :key="'new-' + index"
              :filename="file.name"
              :invalid="invalidFileIndices.includes(index)"
              @click="previewLocalFile(file)"
              @remove="removeFile(index)"
            />
          </div>
          <div
            v-if="invalidFileIndices.length > 0"
            class="gr-file-error-msg"
          >
            พบไฟล์บางส่วนสูญหาย กรุณาตรวจสอบ
          </div>
        </div>
      </div>

      <div
        v-show="showContent"
        class="gr-form-row"
      >
        <span class="gr-label">วันที่รับของ:</span>
        <div class="gr-field-wrapper">
          <DatePickerField
            v-model="receiveDate"
            :is-error="dateSubmitted && !receiveDate"
            :is-required="true"
            :show-error-icon="false"
            :disabled-date="disabledReceiveDate"
          />
        </div>
      </div>

      <div
        v-show="showContent"
        class="gr-form-row"
      >
        <span class="gr-label">รหัสผู้ขาย:</span>
        <span class="gr-info-text">{{
          poDetailData ? poDetailData.vendorCode : "—"
        }}</span>
      </div>

      <div
        v-show="showContent"
        class="gr-form-row"
      >
        <span class="gr-label">ชื่อผู้ขาย:</span>
        <span class="gr-info-text">{{
          poDetailData ? poDetailData.vendorName : "—"
        }}</span>
      </div>
    </div>

    <!-- Tables Section -->
    <div
      v-show="showContent"
      class="gr-table-section"
    >
      <div
        v-if="isLoadingDetail"
        class="gr-loading-row"
      >
        กำลังโหลดข้อมูล...
      </div>
      <template v-if="!isLoadingDetail">
        <!-- PO Items Table -->
        <div
          class="gr-table-outer"
          ref="poTable"
        >
          <table class="gr-table">
            <thead>
              <tr>
                <th class="col-no"></th>
                <th class="col-image"></th>
                <th class="col-product">รหัสสินค้า - ชื่อสินค้า</th>
                <th
                  class="col-received"
                  style="text-align: right"
                >
                  รับแล้ว/ทั้งหมด
                </th>
                <th
                  class="col-remaining"
                  style="text-align: right"
                >
                  เหลือรับ
                </th>
                <th class="col-qty">จำนวนที่รับครั้งนี้</th>
                <th class="col-unit">หน่วยสินค้า</th>
                <th class="col-lot">เลขล็อต</th>
                <th class="col-expire">วันหมดอายุ</th>
                <th class="col-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="tableItems.length === 0">
                <td
                  colspan="10"
                  class="gr-empty-row"
                >
                  ไม่มีรายการสินค้า
                </td>
              </tr>
              <tr
                v-for="(item, index) in tableItems"
                :key="'po-' + index"
                class="gr-item-row"
                :class="{ 'gr-row-disabled': isFullyReceived(item) }"
              >
                <td class="col-no">{{ index + 1 }}.</td>
                <td class="col-image">
                  <ProductImage :src="item.imageUrl || null" />
                </td>
                <td class="col-product">
                  <div class="gr-product-cell">
                    <span>{{ item.code }} - {{ item.name }}</span>
                  </div>
                </td>
                <td
                  class="col-received"
                  style="text-align: right"
                >
                  {{ getDisplayReceived(item) }}/{{ item.total }}
                </td>
                <td
                  class="col-remaining"
                  style="text-align: right"
                  :class="{ 'gr-neg': getRemaining(item) < 0 }"
                >
                  {{ getRemaining(item) }}
                </td>
                <td class="col-qty">
                  <div
                    class="gr-wrap"
                    @keydown.tab.prevent="focusNextQtyInput(index)"
                  >
                    <InputNumberField
                      :adjust-stock="Number(item.qtyReceive) || null"
                      :min="0"
                      :max="Number(item.remaining)"
                      :is-required="true"
                      :allow-decimal="false"
                      :show-buttons="true"
                      :show-error-icon="false"
                      :disabled="isFullyReceived(item)"
                      :invalid="hasQtyError(item)"
                      placeholder="กรอกจำนวน"
                      :on-change="
                        val => {
                          item.qtyReceive = val != null ? String(val) : '';
                        }
                      "
                    />
                  </div>
                </td>
                <td class="col-unit">{{ item.unit }}</td>
                <td class="col-lot">
                  <b-form-input
                    v-model="item.lotNo"
                    class="gr-lot-input"
                    :disabled="isFullyReceived(item)"
                    placeholder="กรอกเลขล็อต"
                  />
                </td>
                <td class="col-expire">
                  <DatePickerField
                    v-model="item.expireDate"
                    :disabled="isFullyReceived(item)"
                  />
                </td>
                <td class="col-action">
                  <div class="gr-delete-wrap">
                    <button
                      class="gr-delete-btn"
                      :disabled="isFullyReceived(item) || isChainClinic"
                      @click="removeItem(index)"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M4.28571 20.125C4.28571 20.6223 4.4889 21.0992 4.85058 21.4508C5.21226 21.8025 5.7028 22 6.21429 22H17.7857C18.2972 22 18.7877 21.8025 19.1494 21.4508C19.5111 21.0992 19.7143 20.6223 19.7143 20.125V7.00001H4.28571V20.125ZM15.2143 10.125C15.2143 9.95925 15.282 9.80028 15.4026 9.68306C15.5231 9.56585 15.6866 9.50001 15.8571 9.50001C16.0276 9.50001 16.1912 9.56585 16.3117 9.68306C16.4323 9.80028 16.5 9.95925 16.5 10.125V18.875C16.5 19.0408 16.4323 19.1997 16.3117 19.3169C16.1912 19.4342 16.0276 19.5 15.8571 19.5C15.6866 19.5 15.5231 19.4342 15.4026 19.3169C15.282 19.1997 15.2143 19.0408 15.2143 18.875V10.125ZM11.3571 10.125C11.3571 9.95925 11.4249 9.80028 11.5454 9.68306C11.666 9.56585 11.8295 9.50001 12 9.50001C12.1705 9.50001 12.334 9.56585 12.4546 9.68306C12.5751 9.80028 12.6429 9.95925 12.6429 10.125V18.875C12.6429 19.0408 12.5751 19.1997 12.4546 19.3169C12.334 19.4342 12.1705 19.5 12 19.5C11.8295 19.5 11.666 19.4342 11.5454 19.3169C11.4249 19.1997 11.3571 19.0408 11.3571 18.875V10.125ZM7.5 10.125C7.5 9.95925 7.56773 9.80028 7.68829 9.68306C7.80885 9.56585 7.97236 9.50001 8.14286 9.50001C8.31335 9.50001 8.47687 9.56585 8.59743 9.68306C8.71798 9.80028 8.78571 9.95925 8.78571 10.125V18.875C8.78571 19.0408 8.71798 19.1997 8.59743 19.3169C8.47687 19.4342 8.31335 19.5 8.14286 19.5C7.97236 19.5 7.80885 19.4342 7.68829 19.3169C7.56773 19.1997 7.5 19.0408 7.5 18.875V10.125ZM20.3571 3.25001H15.5357L15.158 2.51954C15.078 2.36337 14.9548 2.23201 14.8022 2.14023C14.6496 2.04844 14.4736 1.99988 14.2942 2.00001H9.70179C9.52274 1.99934 9.34712 2.04772 9.19506 2.1396C9.04299 2.23149 8.92062 2.36316 8.84196 2.51954L8.46429 3.25001H3.64286C3.47236 3.25001 3.30885 3.31585 3.18829 3.43307C3.06773 3.55028 3 3.70925 3 3.87501V5.12501C3 5.29077 3.06773 5.44974 3.18829 5.56695C3.30885 5.68416 3.47236 5.75001 3.64286 5.75001H20.3571C20.5276 5.75001 20.6912 5.68416 20.8117 5.56695C20.9323 5.44974 21 5.29077 21 5.12501V3.87501C21 3.70925 20.9323 3.55028 20.8117 3.43307C20.6912 3.31585 20.5276 3.25001 20.3571 3.25001Z"
                          fill="#EBEBEB"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Bonus Items Section -->
        <template v-if="bonusItems.length > 0">
          <div class="gr-bonus-header">รายการสินค้าแถม</div>
          <div
            class="gr-table-outer gr-bonus-table-outer"
            ref="bonusTable"
          >
            <table class="gr-table">
              <thead>
                <tr>
                  <th class="col-no"></th>
                  <th class="col-image"></th>
                  <th class="col-bonus-product">รหัสสินค้า - ชื่อสินค้า</th>
                  <th class="col-bonus-qty">จำนวนสินค้า</th>
                  <th class="col-bonus-unit">หน่วยสินค้า</th>
                  <th class="col-lot">เลขล็อต</th>
                  <th class="col-expire">วันหมดอายุ</th>
                  <th class="col-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in bonusItems"
                  :key="'bonus-' + index"
                  class="gr-item-row"
                >
                  <td class="col-no">{{ index + 1 }}.</td>
                  <td class="col-image">
                    <ProductImage :src="item.product?.imageUrl || null" />
                  </td>
                  <td class="col-bonus-product">
                    <MultipleSelectField
                      :value="item.product"
                      :options="getBonusProductOptions(index)"
                      :is-required="true"
                      :is-error="item.touched && !item.product"
                      :show-error-icon="false"
                      track-by="productId"
                      :custom-label="p => `${p.code} - ${p.name}`"
                      placeholder="เลือกสินค้า"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      :allow-empty="false"
                      @input="
                        val => {
                          item.product = val;
                          onBonusProductChange(index);
                        }
                      "
                    />
                  </td>
                  <td class="col-bonus-qty">
                    <div
                      class="gr-wrap"
                      @keydown.tab.prevent="focusNextBonusQtyInput(index)"
                    >
                      <InputNumberField
                        :adjust-stock="Number(item.qty) || null"
                        :min="0"
                        :is-required="true"
                        :allow-decimal="false"
                        :show-buttons="true"
                        :show-error-icon="false"
                        :disabled="!item.product"
                        :invalid="isBonusQtyError(item)"
                        placeholder="จำนวนสินค้า"
                        :on-change="
                          val => {
                            item.qty = val != null ? String(val) : '';
                          }
                        "
                      />
                    </div>
                  </td>
                  <td class="col-bonus-unit">
                    <MultipleSelectField
                      v-model="item.unit"
                      :options="getBonusUnitOptions(item)"
                      :is-required="!!item.product"
                      :is-error="isBonusUnitError(item)"
                      :show-error-icon="false"
                      track-by="uomId"
                      :custom-label="u => u.name"
                      placeholder="เลือกหน่วยสินค้า"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      :disabled="!item.product"
                      :allow-empty="false"
                    />
                  </td>
                  <td class="col-lot">
                    <b-form-input
                      v-model="item.lotNo"
                      class="gr-lot-input"
                      :disabled="!item.product"
                      placeholder="กรอกเลขล็อต"
                    />
                  </td>
                  <td class="col-expire">
                    <DatePickerField
                      v-model="item.expireDate"
                      :disabled="!item.product"
                    />
                  </td>
                  <td class="col-action">
                    <div class="gr-delete-wrap">
                      <button
                        class="gr-delete-btn"
                        @click="removeBonusItem(index)"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M4.28571 20.125C4.28571 20.6223 4.4889 21.0992 4.85058 21.4508C5.21226 21.8025 5.7028 22 6.21429 22H17.7857C18.2972 22 18.7877 21.8025 19.1494 21.4508C19.5111 21.0992 19.7143 20.6223 19.7143 20.125V7.00001H4.28571V20.125ZM15.2143 10.125C15.2143 9.95925 15.282 9.80028 15.4026 9.68306C15.5231 9.56585 15.6866 9.50001 15.8571 9.50001C16.0276 9.50001 16.1912 9.56585 16.3117 9.68306C16.4323 9.80028 16.5 9.95925 16.5 10.125V18.875C16.5 19.0408 16.4323 19.1997 16.3117 19.3169C16.1912 19.4342 16.0276 19.5 15.8571 19.5C15.6866 19.5 15.5231 19.4342 15.4026 19.3169C15.282 19.1997 15.2143 19.0408 15.2143 18.875V10.125ZM11.3571 10.125C11.3571 9.95925 11.4249 9.80028 11.5454 9.68306C11.666 9.56585 11.8295 9.50001 12 9.50001C12.1705 9.50001 12.334 9.56585 12.4546 9.68306C12.5751 9.80028 12.6429 9.95925 12.6429 10.125V18.875C12.6429 19.0408 12.5751 19.1997 12.4546 19.3169C12.334 19.4342 12.1705 19.5 12 19.5C11.8295 19.5 11.666 19.4342 11.5454 19.3169C11.4249 19.1997 11.3571 19.0408 11.3571 18.875V10.125ZM7.5 10.125C7.5 9.95925 7.56773 9.80028 7.68829 9.68306C7.80885 9.56585 7.97236 9.50001 8.14286 9.50001C8.31335 9.50001 8.47687 9.56585 8.59743 9.68306C8.71798 9.80028 8.78571 9.95925 8.78571 10.125V18.875C8.78571 19.0408 8.71798 19.1997 8.59743 19.3169C8.47687 19.4342 8.31335 19.5 8.14286 19.5C7.97236 19.5 7.80885 19.4342 7.68829 19.3169C7.56773 19.1997 7.5 19.0408 7.5 18.875V10.125ZM20.3571 3.25001H15.5357L15.158 2.51954C15.078 2.36337 14.9548 2.23201 14.8022 2.14023C14.6496 2.04844 14.4736 1.99988 14.2942 2.00001H9.70179C9.52274 1.99934 9.34712 2.04772 9.19506 2.1396C9.04299 2.23149 8.92062 2.36316 8.84196 2.51954L8.46429 3.25001H3.64286C3.47236 3.25001 3.30885 3.31585 3.18829 3.43307C3.06773 3.55028 3 3.70925 3 3.87501V5.12501C3 5.29077 3.06773 5.44974 3.18829 5.56695C3.30885 5.68416 3.47236 5.75001 3.64286 5.75001H20.3571C20.5276 5.75001 20.6912 5.68416 20.8117 5.56695C20.9323 5.44974 21 5.29077 21 5.12501V3.87501C21 3.70925 20.9323 3.55028 20.8117 3.43307C20.6912 3.31585 20.5276 3.25001 20.3571 3.25001Z"
                            fill="#A3A4A6"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <button
          class="gr-add-btn"
          @click="addBonusItem"
        >
          <span class="gr-add-plus">+</span>
          เพิ่มสินค้าแถม
        </button>
      </template>
    </div>

    <template v-slot:modal-footer>
      <div class="gr-footer-btns">
        <b-button
          v-if="isEditMode"
          variant="danger"
          class="btn-delete"
          @click="deleteGr"
        >
          ยกเลิกรายการ
        </b-button>
        <b-button
          variant="primary"
          class="btn-confirm"
          :disabled="isLoading"
          @click="confirm"
        >
          {{ isEditMode ? "แก้ไขรายการ" : "สร้างรายการ" }}
        </b-button>
      </div>
    </template>
    <Loading v-if="isLoading" />
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import BigNumber from "bignumber.js";
import DatePickerField from "@/components/table/stock/fields/DatePickerField.vue";
import MultipleSelectField from "@/components/table/stock/fields/MultipleSelectField.vue";
import InputNumberField from "@/components/table/stock/fields/InputNumberField.vue";
import { ClinicType, Status } from "@/constants/productStock";
import { ROLES } from "@/constants/user";
import moment from "moment";
import Loading from "@/components/Loading";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import GrFileChip from "@/components/modal/ProductAndStock/GrFileChip.vue";
import { convertBaseToLineUom } from "@/utils/uomConversion";

export default {
  name: "GoodsReceiveSelectPOModal",
  components: {
    DatePickerField,
    MultipleSelectField,
    InputNumberField,
    Loading,
    ProductImage,
    GrFileChip,
  },
  data() {
    return {
      mode: "create", // "create" | "edit"
      editGrId: null,
      editGrData: null,
      selectedPoId: null,
      submitted: false,
      dateSubmitted: false,
      poDropdownList: [],
      poDetailData: null,
      isLoading: false,
      isLoadingDetail: false,
      receiveDate: null,
      uploadedFiles: [],
      existingFiles: [],
      invalidFileIndices: [],
      tableItems: [],
      bonusItems: [],
    };
  },
  computed: {
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    isEditMode() {
      return this.mode === "edit";
    },
    showContent() {
      return this.isEditMode || !!this.selectedPoId;
    },
    ...mapGetters({
      isAccessMenu: "moduleUser/getIsAccessMenu",
      getStockAdjustmentList: "moduleProductInventory/getStockAdjustmentList",
    }),
    isSpecialStockAdjuster() {
      return this.isAccessMenu([ROLES.ROLE_SPECIAL_STOCK_ADJUSTER]);
    },
    isChainClinic() {
      const type = this.$store.state.moduleBranch?.branchInfo?.clinic?.type;
      return type === ClinicType.CHAIN || type === "CHAIN_NO_ERP";
    },
    poOptions() {
      return this.poDropdownList.map(po => ({ id: po.id, no: po.no }));
    },
    selectedPo() {
      return this.poOptions.find(po => po.id === this.selectedPoId) || null;
    },
    productList() {
      return (
        this.$store.getters["moduleProductInventory/getProductList"] || []
      ).map(p => {
        const baseUnit = p.productUnit;
        const baseUomId = baseUnit?.id;
        const baseEntry = baseUnit
          ? [
              {
                uomId: baseUnit.id,
                code: baseUnit.code || "",
                name: baseUnit.description2 || baseUnit.description || "",
              },
            ]
          : [];
        const conversionEntries = (p.productConversionUnitItems || [])
          .filter(c => c.uomId !== baseUomId)
          .map(c => ({
            uomId: c.uomId,
            code: c.uom?.code || "",
            name: c.uom?.description2 || c.uom?.description || "",
          }));
        return {
          productId: p.productId,
          code: p.productCode,
          name: p.productNameMain,
          imageUrl: p.imageUrl || null,
          baseUom: baseEntry[0] || null,
          uomConversions: [...baseEntry, ...conversionEntries],
        };
      });
    },
    selectableProductIds() {
      return new Set(
        (
          this.$store.getters["moduleProductInventory/getProductList"] || []
        )
          .filter(p => p.productStatus !== Status.ARCHIVED)
          .map(p => p.productId)
      );
    },
    allActiveQtyAreZero() {
      const active = this.tableItems.filter(
        item => !this.isFullyReceived(item)
      );
      if (active.length === 0) return false;
      return active.every(
        item => !item.qtyReceive || new BigNumber(item.qtyReceive).isZero()
      );
    },
  },
  watch: {
    receiveDate() {
      this.dateSubmitted = false;
    },
    async selectedPoId(id) {
      this.tableItems = [];
      this.bonusItems = [];
      this.submitted = false;
      this.dateSubmitted = false;
      this.receiveDate = new Date();
      this.uploadedFiles = [];
      this.existingFiles = [];
      this.invalidFileIndices = [];
      this.poDetailData = null;
      if (!id) return;

      this.isLoadingDetail = true;
      try {
        const detail = await this.fetchPurchaseOrderById({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          id,
        });
        if (detail) {
          this.poDetailData = detail;
          this.tableItems = (detail.items || []).map(item => {
            const productData = this.productList.find(
              p => p.productId === item.productId
            );
            return {
              productId: item.productId,
              uomId: item.uomId,
              unitCost: item.unitCost,
              code: item.productCode,
              name: item.productName,
              imageUrl: productData?.imageUrl || null,
              received: item.qtyReceived,
              total: item.qtyTotal,
              remaining: convertBaseToLineUom(
                item.remainQty,
                item.qtyPerUnitOfMeasure
              ),
              unit: item.uomName,
              qtyReceive: "",
              lotNo: "",
              expireDate: null,
            };
          });
        }
      } catch (err) {
        console.error("fetchPurchaseOrderById failed", err);
      } finally {
        this.isLoadingDetail = false;
      }
    },
  },
  methods: {
    ...mapActions({
      fetchPurchaseOrderById: "modulePurchaseOrder/fetchPurchaseOrderById",
      fetchPurchaseOrderDropdown:
        "modulePurchaseOrder/fetchPurchaseOrderDropdown",
      fetchGoodsReceiveById: "moduleGoodsReceive/fetchGoodsReceiveById",
      createGoodsReceive: "moduleGoodsReceive/createGoodsReceive",
      updateGoodsReceive: "moduleGoodsReceive/updateGoodsReceive",
      deleteGoodsReceive: "moduleGoodsReceive/deleteGoodsReceive",
      fetchMediaFileById: "moduleGoodsReceive/fetchMediaFileById",
      deleteMediaFile: "moduleGoodsReceive/deleteMediaFile",
      uploadGrFilesToS3: "moduleGoodsReceive/uploadGrFilesToS3",
      fetchStockAdjustmentList:
        "moduleProductInventory/fetchStockAdjustmentList",
    }),

    showToast(message, title, variant = "danger", delay = 3000) {
      this.$root.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
        toaster: "b-toaster-bottom-left",
        autoHideDelay: delay,
      });
    },

    // ── Helpers ──────────────────────────────────────────────────────────────

    focusNextQtyInput(currentIndex) {
      const inputs = this.$refs.poTable?.querySelectorAll(".col-qty textarea");
      if (!inputs) return;
      for (let i = currentIndex + 1; i < inputs.length; i++) {
        if (!inputs[i].disabled) {
          inputs[i].focus();
          return;
        }
      }
    },
    focusNextBonusQtyInput(currentIndex) {
      const inputs = this.$refs.bonusTable?.querySelectorAll(
        ".col-bonus-qty textarea"
      );
      if (!inputs) return;
      for (let i = currentIndex + 1; i < inputs.length; i++) {
        if (!inputs[i].disabled) {
          inputs[i].focus();
          return;
        }
      }
    },
    disabledReceiveDate(date) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (date > today) return true;
      if (this.isSpecialStockAdjuster) return false;

      // Grace period: on day 1-3 of the month, still allow backdating into
      // the previous month (e.g. catching up on a late GR entry before the
      // books close). From day 4 onward, only the current month is allowed.
      // Matches the same grace period used for stock adjustments (withdraw/
      // return/general) in StockAdjustment.vue's isDateDisabled.
      const BACKDATE_GRACE_DAY = 3;
      const monthOffset = today.getDate() <= BACKDATE_GRACE_DAY ? -1 : 0;
      const startOfAllowedRange = new Date(
        today.getFullYear(),
        today.getMonth() + monthOffset,
        1
      );
      return date < startOfAllowedRange;
    },

    parseIsoDate(str) {
      if (!str) return null;
      const datePart = String(str).split("T")[0];
      const parts = datePart.split("-").map(Number);
      if (parts.length < 3 || isNaN(parts[0])) return null;
      return new Date(parts[0], parts[1] - 1, parts[2]);
    },

    toIsoDate(val) {
      return val instanceof Date ? moment(val).add(7, "h").toISOString() : null;
    },

    // ── Row state ─────────────────────────────────────────────────────────────

    isFullyReceived(item) {
      return new BigNumber(item.remaining || 0).isLessThanOrEqualTo(0);
    },
    getRemaining(item) {
      return new BigNumber(item.remaining || 0)
        .minus(item.qtyReceive || 0)
        .toNumber();
    },
    getDisplayReceived(item) {
      return new BigNumber(item.total || 0)
        .minus(item.remaining || 0)
        .plus(item.qtyReceive || 0)
        .toNumber();
    },

    // ── Validation helpers ────────────────────────────────────────────────────

    hasQtyError(item) {
      if (new BigNumber(item.qtyReceive || 0).isLessThan(0)) return true;
      if (!this.isFullyReceived(item) && this.getRemaining(item) < 0)
        return true;
      if (!this.submitted || this.isFullyReceived(item)) return false;
      if (
        item.qtyReceive === "" ||
        item.qtyReceive === null ||
        item.qtyReceive === undefined
      )
        return true;
      if (
        this.allActiveQtyAreZero &&
        new BigNumber(item.qtyReceive || 0).isZero()
      )
        return true;
      return false;
    },
    isBonusQtyError(item) {
      if (!item.touched || !item.product) return false;
      return !item.qty || new BigNumber(item.qty || 0).isZero();
    },
    isBonusUnitError(item) {
      return item.touched && !!item.product && !item.unit;
    },
    validateForm(isEdit, selectedPoId, receiveDate) {
      if (!isEdit && !selectedPoId) return false;
      if (!receiveDate) return false;
      if (this.tableItems.some(item => this.getRemaining(item) < 0))
        return false;
      const hasEmptyQty = this.tableItems.some(
        item =>
          !this.isFullyReceived(item) &&
          (item.qtyReceive === "" ||
            item.qtyReceive === null ||
            item.qtyReceive === undefined)
      );
      if (hasEmptyQty) return false;
      if (this.allActiveQtyAreZero) return false;
      const hasBonusError = this.bonusItems.some(
        item =>
          !item.product ||
          !item.qty ||
          new BigNumber(item.qty || 0).isZero() ||
          !item.unit
      );
      return !hasBonusError;
    },

    // ── Body builder ──────────────────────────────────────────────────────────

    buildGrBody({ isEdit, editGr, selectedPoId, detail, po, receiveDate }) {
      const poProducts = this.tableItems
        .filter(
          item =>
            !this.isFullyReceived(item) &&
            new BigNumber(item.qtyReceive || 0).isGreaterThan(0)
        )
        .map(item => ({
          productId: item.productId,
          productType: "NORMAL",
          qtyUom: Number(item.qtyReceive),
          uomId: item.uomId,
          totalCost: new BigNumber(item.qtyReceive || 0)
            .times(item.unitCost || 0)
            .decimalPlaces(2, BigNumber.ROUND_DOWN)
            .toNumber(),
          lotNo: item.lotNo || "",
          expiredDt: this.toIsoDate(item.expireDate),
        }));

      const bonusProducts = this.bonusItems.map(item => ({
        productId: item.product.productId,
        productType: "FREE",
        qtyUom: Number(item.qty),
        uomId: item.unit?.uomId || 0,
        totalCost: 0,
        lotNo: item.lotNo || "",
        expiredDt: this.toIsoDate(item.expireDate),
      }));

      const receivedDt = (() => {
        const dateObj = receiveDate || new Date();
        const selectedDate = moment(dateObj).format("YYYY-MM-DD");
        const currentTimeUtc = moment().utc().format("HH:mm:ss.SSS[Z]");
        return `${selectedDate}T${currentTimeUtc}`;
      })();
      const goodReceiveProducts = [...poProducts, ...bonusProducts];

      const filesMetadata = this.uploadedFiles.map(file => ({
        contentType: file.type,
        fileSize: file.size,
        originalName: file.name,
      }));

      if (isEdit) {
        return {
          vendorNo: editGr.vendorCode,
          vendorName: editGr.vendorName,
          refDocNo: editGr.poRefCode,
          receivedDt,
          poId: editGr.poId,
          goodReceiveProducts,
          filesMetadata,
        };
      }
      return {
        vendorNo: detail.vendorCode,
        vendorName: detail.vendorName,
        refDocNo: po.no,
        receivedDt,
        poId: selectedPoId,
        goodReceiveProducts,
        filesMetadata,
      };
    },

    // ── Bonus item actions ────────────────────────────────────────────────────

    getBonusProductOptions(index) {
      const ownId = this.bonusItems[index]?.product?.productId;
      const otherIds = this.bonusItems
        .filter((_, i) => i !== index)
        .map(b => b.product?.productId)
        .filter(id => id != null);
      return this.productList
        .filter(
          p =>
            (this.selectableProductIds.has(p.productId) ||
              p.productId === ownId) &&
            (!otherIds.includes(p.productId) || p.productId === ownId)
        )
        .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
    },
    getBonusUnitOptions(item) {
      const conversions = item.product?.uomConversions || [];
      if (conversions.length === 0 && item.product?.baseUom) {
        return [item.product.baseUom];
      }
      return conversions;
    },
    onBonusProductChange(index) {
      const item = this.bonusItems[index];
      item.qty = "";
      item.lotNo = "";
      item.expireDate = "";
      const units = item.product?.uomConversions || [];
      item.unit = units.length === 1 ? units[0] : null;
    },
    addBonusItem() {
      this.bonusItems.push({
        product: null,
        qty: "",
        unit: null,
        lotNo: "",
        expireDate: "",
        touched: false,
      });
    },
    removeBonusItem(index) {
      this.bonusItems.splice(index, 1);
    },
    removeItem(index) {
      this.tableItems.splice(index, 1);
    },

    // ── Modal lifecycle ───────────────────────────────────────────────────────

    async show(opts = {}) {
      this.selectedPoId = null;
      this.submitted = false;
      this.dateSubmitted = false;
      this.receiveDate = new Date();
      this.uploadedFiles = [];
      this.existingFiles = [];
      this.invalidFileIndices = [];
      this.tableItems = [];
      this.bonusItems = [];
      this.poDropdownList = [];
      this.poDetailData = null;
      this.editGrId = null;
      this.editGrData = null;

      if (
        !this.getStockAdjustmentList ||
        this.getStockAdjustmentList.length === 0
      ) {
        try {
          await this.fetchStockAdjustmentList({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
          });
        } catch (err) {
          console.error(
            "fetchStockAdjustmentList failed in GoodsReceiveSelectPOModal",
            err
          );
        }
      }

      if (opts.goodsReceive) {
        this.mode = "edit";
        this.editGrId = opts.goodsReceive.id;
        this.$bvModal.show("goodsReceiveSelectPOModal");
        await this.loadEditData(opts.goodsReceive);
      } else {
        this.mode = "create";
        this.$bvModal.show("goodsReceiveSelectPOModal");
        try {
          const data = await this.fetchPurchaseOrderDropdown({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
          });
          this.poDropdownList = data || [];
        } catch (err) {
          console.error("fetchPurchaseOrderDropdown failed", err);
        }
      }
    },
    async loadEditData(goodsReceive) {
      this.isLoadingDetail = true;
      try {
        const grDetail = await this.fetchGoodsReceiveById({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          id: goodsReceive.id,
        });
        this.editGrData = grDetail;
        this.receiveDate = grDetail.receivedDt
          ? this.parseIsoDate(grDetail.receivedDt)
          : new Date();

        this.existingFiles = Array.isArray(grDetail.files)
          ? [...grDetail.files]
          : [];

        // A line an earlier edit replaced is soft-deleted and must not feed the
        // form: pre-filling from it shows a stale qty and, because the PO no
        // longer counts it as claimed, inflates how much can be received.
        const liveItems = (grDetail.items || []).filter(g => !g.deletedDt);
        const grNormalItems = liveItems.filter(g => g.productType !== "FREE");
        const grFreeItems = liveItems.filter(g => g.productType === "FREE");

        if (grDetail.poId) {
          const poDetail = await this.fetchPurchaseOrderById({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            id: grDetail.poId,
          });
          if (poDetail) {
            this.poDetailData = poDetail;
            this.tableItems = (poDetail.items || []).map(poItem => {
              const grItem = grNormalItems.find(
                g => g.productId === poItem.productId
              );
              const productData = this.productList.find(
                p => p.productId === poItem.productId
              );
              return {
                productId: poItem.productId,
                uomId: poItem.uomId,
                unitCost: poItem.unitCost,
                code: poItem.productCode,
                name: poItem.productName,
                imageUrl: productData?.imageUrl || null,
                received: poItem.qtyReceived,
                total: poItem.qtyTotal,
                remaining: convertBaseToLineUom(
                  new BigNumber(poItem.remainQty || 0)
                    .plus(grItem?.qtyBaseUom || 0)
                    .toNumber(),
                  poItem.qtyPerUnitOfMeasure
                ),
                unit: poItem.uomName,
                qtyReceive: grItem ? String(grItem.quantity) : "",
                lotNo: grItem?.lotNumber || "",
                expireDate: grItem?.expiredDt
                  ? this.parseIsoDate(grItem.expiredDt)
                  : null,
              };
            });
          }
        } else {
          this.tableItems = grNormalItems.map(item => {
            const productData = this.productList.find(
              p => p.productId === item.productId
            );
            return {
              productId: item.productId,
              uomId: item.uomId,
              unitCost: 0,
              code: item.productCode,
              name: item.productNameMain,
              imageUrl: productData?.imageUrl || null,
              received: 0,
              total: item.quantity,
              remaining: 0,
              unit: item.uomName,
              qtyReceive: String(item.quantity),
              lotNo: item.lotNumber || "",
              expireDate: item.expiredDt
                ? this.parseIsoDate(item.expiredDt)
                : null,
            };
          });
        }

        this.bonusItems = grFreeItems.map(grItem => {
          const product = this.productList.find(
            p => p.productId === grItem.productId
          );
          const unit =
            product?.uomConversions.find(u => u.uomId === grItem.uomId) || null;
          return {
            product: product || {
              productId: grItem.productId,
              code: grItem.productCode,
              name: grItem.productNameMain,
              baseUom: grItem.uomId
                ? { uomId: grItem.uomId, name: grItem.uomName || "", code: "" }
                : null,
              uomConversions: [],
            },
            qty: String(grItem.quantity),
            unit,
            lotNo: grItem.lotNumber || "",
            expireDate: grItem.expiredDt
              ? this.parseIsoDate(grItem.expiredDt)
              : null,
          };
        });
      } catch (err) {
        console.error("loadEditData failed", err);
      } finally {
        this.isLoadingDetail = false;
      }
    },
    close() {
      this.selectedPoId = null;
      this.submitted = false;
      this.dateSubmitted = false;
      this.mode = "create";
      this.editGrId = null;
      this.editGrData = null;
      this.bonusItems = [];
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const selected = Array.from(e.target.files || []);
      if (selected.length === 0) return;

      const MAX_SIZE = 15 * 1024 * 1024;
      const MAX_FILES = 10;
      const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png"];
      const ALLOWED_MIME = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      const invalidType = selected.filter(f => {
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        return (
          !ALLOWED_EXT.includes(ext) &&
          !ALLOWED_MIME.includes((f.type || "").toLowerCase())
        );
      });
      if (invalidType.length > 0) {
        this.showToast(
          "รองรับเฉพาะไฟล์ PDF, JPG, JPEG, PNG เท่านั้น",
          "ไฟล์ไม่ถูกต้อง",
          "danger",
          4000
        );
        e.target.value = "";
        return;
      }

      const tooLarge = selected.filter(f => f.size > MAX_SIZE);
      if (tooLarge.length > 0) {
        this.showToast(
          "ขนาดไฟล์เกิน 15 MB กรุณาเลือกไฟล์ใหม่",
          "ไฟล์มีขนาดใหญ่เกินไป",
          "danger",
          4000
        );
        e.target.value = "";
        return;
      }

      const totalFiles =
        this.existingFiles.length + this.uploadedFiles.length + selected.length;
      if (totalFiles > MAX_FILES) {
        this.showToast(
          "อัปโหลดได้สูงสุด 10 ไฟล์",
          "จำนวนไฟล์เกินกำหนด",
          "danger",
          4000
        );
        e.target.value = "";
        return;
      }

      const existingNames = new Set([
        ...this.existingFiles.map(f => f.originalName),
        ...this.uploadedFiles.map(f => f.name),
      ]);
      const unique = selected.filter(f => !existingNames.has(f.name));
      if (unique.length === 0) {
        e.target.value = "";
        return;
      }
      this.uploadedFiles = [...this.uploadedFiles, ...unique];
      e.target.value = "";
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
      this.invalidFileIndices = this.invalidFileIndices
        .filter(i => i !== index)
        .map(i => (i > index ? i - 1 : i));
    },
    checkFileAccessible(file) {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(true);
        reader.onerror = () => resolve(false);
        reader.readAsArrayBuffer(file.slice(0, 1));
      });
    },
    async validateUploadedFiles() {
      const results = await Promise.all(
        this.uploadedFiles.map(f => this.checkFileAccessible(f))
      );
      this.invalidFileIndices = results
        .map((ok, i) => (ok ? null : i))
        .filter(i => i !== null);
      return this.invalidFileIndices.length === 0;
    },
    previewLocalFile(file) {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    },
    async previewFile(id) {
      if (!id) return;
      const newTab = window.open("", "_blank");
      try {
        const mediaURL = await this.fetchMediaFileById({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          id,
        });
        if (mediaURL && newTab) {
          newTab.location.href = mediaURL;
        } else {
          if (newTab) newTab.close();
          this.showToast("ไม่พบไฟล์", "ไม่สำเร็จ");
        }
      } catch (err) {
        console.error("previewFile failed", err);
        if (newTab) newTab.close();
        this.showToast("เปิดไฟล์ไม่สำเร็จ", "ไม่สำเร็จ");
      }
    },
    async removeExistingFile(id) {
      if (!id) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "ยืนยันการลบไฟล์นี้?",
        {
          title: "ยืนยันการลบ",
          okVariant: "danger",
          okTitle: "ลบ",
          cancelTitle: "ยกเลิก",
          centered: true,
        }
      );
      if (!confirmed) return;
      try {
        await this.deleteMediaFile({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          id,
        });
        this.existingFiles = this.existingFiles.filter(f => f.id !== id);
        this.showToast("ลบไฟล์สำเร็จ", "สำเร็จ", "success");
      } catch (err) {
        console.error("deleteMediaFile failed", err);
        this.showToast(
          "ลบไฟล์ไม่สำเร็จ กรุณาลองใหม่",
          "ไม่สำเร็จ",
          "danger",
          4000
        );
      }
    },

    // ── Submit / Delete ───────────────────────────────────────────────────────

    async confirm() {
      // Snapshot reactive state before any async work so that
      // @hide → close() resetting this.mode mid-flight cannot affect the branch.
      const isEdit = this.mode === "edit";
      const editId = this.editGrId;
      const editGr = this.editGrData;
      const selectedPoId = this.selectedPoId;
      const detail = this.poDetailData;
      const po = this.selectedPo;
      const receiveDate = this.receiveDate;

      this.submitted = true;
      this.dateSubmitted = true;
      this.bonusItems.forEach(item => {
        item.touched = true;
      });

      const [formOk, filesOk] = await Promise.all([
        Promise.resolve(this.validateForm(isEdit, selectedPoId, receiveDate)),
        this.uploadedFiles.length > 0
          ? this.validateUploadedFiles()
          : Promise.resolve(true),
      ]);

      if (!formOk || !filesOk) {
        if (!filesOk) {
          this.$nextTick(() => {
            const el = this.$refs.fileSection;
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          });
        }
        return;
      }

      this.isLoading = true;
      const body = this.buildGrBody({
        isEdit,
        editGr,
        selectedPoId,
        detail,
        po,
        receiveDate,
      });
      const { clinicUrl, branchUrl } = this;

      if (isEdit) {
        try {
          const res = await this.updateGoodsReceive({
            clinicUrl,
            branchUrl,
            id: editId,
            body,
          });
          const uploadUrls = this.extractUploadUrls(res);
          const uploadOk = await this.handleFileUpload(uploadUrls);
          if (!uploadOk) {
            this.showUpdateFailToast();
            return;
          }
          this.$bvModal.hide("goodsReceiveSelectPOModal");
          this.$emit("updated");
        } catch (err) {
          console.error("updateGoodsReceive failed", err);
          this.$emit("error", "แก้ไขใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่");
        } finally {
          this.isLoading = false;
        }
      } else {
        let createdId = null;
        try {
          const res = await this.createGoodsReceive({
            clinicUrl,
            branchUrl,
            body,
          });
          createdId = this.extractCreatedGrId(res);
          const uploadUrls = this.extractUploadUrls(res);
          const uploadOk = await this.handleFileUpload(uploadUrls);
          if (!uploadOk) {
            if (createdId) {
              try {
                await this.deleteGoodsReceive({
                  clinicUrl,
                  branchUrl,
                  id: createdId,
                });
              } catch (delErr) {
                console.error("rollback deleteGoodsReceive failed", delErr);
              }
            }
            this.showCreateFailToast();
            return;
          }
          this.$bvModal.hide("goodsReceiveSelectPOModal");
          this.$emit("created");
        } catch (err) {
          console.error("createGoodsReceive failed", err);
          this.showCreateFailToast();
        } finally {
          this.isLoading = false;
        }
      }
    },
    extractUploadUrls(res) {
      const payload = res?.data ?? res;
      if (Array.isArray(payload?.uploadUrls)) return payload.uploadUrls;
      if (Array.isArray(payload?.data?.uploadUrls)) {
        return payload.data.uploadUrls;
      }
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      return [];
    },
    async handleFileUpload(uploadUrls) {
      if (this.uploadedFiles.length === 0) {
        return true;
      }
      if (!uploadUrls || uploadUrls.length === 0) {
        return false;
      }
      try {
        return await this.uploadGrFilesToS3({
          uploadUrls,
          files: this.uploadedFiles,
        });
      } catch (err) {
        console.error("uploadGrFilesToS3 failed", err);
        return false;
      }
    },
    extractCreatedGrId(res) {
      const payload = res?.data ?? res;
      return (
        payload?.id ||
        payload?.data?.id ||
        payload?.goodReceive?.id ||
        payload?.data?.goodReceive?.id ||
        null
      );
    },
    showCreateFailToast() {
      this.showToast(
        "สร้างใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่",
        "ไม่สำเร็จ",
        "danger",
        4000
      );
    },
    showUpdateFailToast() {
      this.showToast(
        "แก้ไขใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่",
        "ไม่สำเร็จ",
        "danger",
        4000
      );
    },
    async deleteGr() {
      try {
        await this.deleteGoodsReceive({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          id: this.editGrId,
        });
        this.$bvModal.hide("goodsReceiveSelectPOModal");
        this.$emit("deleted");
      } catch (err) {
        console.error("deleteGoodsReceive failed", err);
        this.$emit("error", "ยกเลิกใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่");
      }
    },
  },
};
</script>

<style scoped>
/* ── Modal overrides ── */
::v-deep .gr-select-po-modal .modal-dialog {
  max-width: 1400px;
  width: 1400px;
}

::v-deep .gr-select-po-modal .modal-content {
  height: auto;
  min-height: unset;
}

::v-deep .gr-select-po-modal .modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 20px 32px;
}

::v-deep .gr-select-po-modal .modal-body {
  padding: 0;
  overflow-y: auto;
}

::v-deep .gr-select-po-modal .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 12px 24px;
}

.gr-footer-btns {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
}

/* ── Form section ── */
.gr-form-section {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 20px 32px;
  border-bottom: 1px solid #dee2e6;
}

.gr-form-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
}

.gr-label {
  width: 200px;
  flex-shrink: 0;
  text-align: right;
  font-weight: 600;
  font-size: 15px;
  color: #212529;
  white-space: nowrap;
}

.gr-label-top {
  align-self: flex-start;
  padding-top: 10px;
}

.gr-field-wrapper {
  flex: 1;
}

.gr-select-wrapper {
  position: relative;
  width: 100%;
}

.gr-select-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #495057;
}

.gr-select {
  appearance: none;
  -webkit-appearance: none;
  background-color: #ebf3fd;
  background-image: none !important;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  height: 38px;
  color: #212529;
  width: 100%;
  padding: 0 36px 0 14px;
}

.gr-select.has-error {
  border-color: #dc3545;
}

.gr-error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

/* File upload */
.gr-upload-box {
  display: flex;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
}

.gr-upload-text-area {
  flex: 1;
  height: 38px;
  display: flex;
  align-items: center;
  padding: 0 14px;
}

.gr-upload-placeholder {
  color: #6d747d;
}

.gr-file-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  margin-top: 8px;
  margin-bottom: 4px;
}

.gr-file-error-msg {
  margin-top: 6px;
  font-size: 13px;
  color: #dc3545;
}

.gr-upload-action {
  flex-shrink: 0;
  height: 38px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  background-color: #f2f2f2;
  color: #3e495b;
  white-space: nowrap;
}

.gr-file-input {
  display: none;
}

.gr-info-text {
  font-size: 15px;
  color: #212529;
  word-break: break-word;
}

/* ── Loading ── */
.gr-loading-row {
  padding: 24px 0;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
}

/* ── Table section ── */
.gr-table-section {
  padding: 20px 32px 24px;
}

.gr-table-outer {
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 16px;
  overflow-x: auto;
}

.gr-table {
  width: 100%;
  border-collapse: collapse;
}

.gr-table thead tr {
  background-color: #e9ecef;
}

.gr-table thead th {
  padding: 8px;
  font-weight: 600;
  color: #212529;
  border-bottom: 1px solid #dee2e6;
  border-top: none;
  border-left: none;
  border-right: none;
  white-space: nowrap;
}

.gr-table tbody td {
  padding: 8px;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
  color: #212529;
}

.gr-table tbody tr:last-child td {
  border-bottom: none;
}

.gr-empty-row {
  text-align: center;
  color: #adb5bd;
  padding: 32px !important;
}

/* Fully-received row — grey out everything */
.gr-row-disabled td {
  background-color: #f8f9fa !important;
  color: #adb5bd !important;
}

.gr-row-disabled:hover td {
  background-color: #f8f9fa !important;
}

/* Column widths */
.col-no {
  width: 40px;
  text-align: left;
}
.col-product {
  text-align: left;
}
.col-received {
  width: 140px;
}
.col-remaining {
  width: 100px;
}
.col-qty {
  width: 200px;
  text-align: left;
}
.col-image {
  width: 56px;
  text-align: left;
}
.col-unit {
  width: 140px;
  text-align: left;
}
.col-lot {
  width: 130px;
  text-align: left;
}
.col-expire {
  width: 168px;
  text-align: left;
}
.col-action {
  width: 64px;
  text-align: center;
}

/* Bonus table columns */
.col-bonus-product {
  text-align: left;
}
.col-bonus-qty {
  width: 210px;
  text-align: left;
}
.col-bonus-unit {
  width: 190px;
  text-align: left;
}

/* Product cell layout */
.gr-product-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gr-product-thumb {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

/* Negative remaining = red */
.gr-neg {
  color: #dc3545 !important;
  font-weight: 600;
}

/* ── Input wrapper + error icon ── */
.gr-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.gr-err-ic {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid #dc3545;
  color: #dc3545;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.gr-err-ic-sel {
  right: 30px;
}

.gr-invalid {
  border-color: #dc3545 !important;
}

::v-deep .gr-qty-input.gr-invalid {
  padding-right: 32px !important;
}

.gr-qty-input {
  height: 38px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #ebf3fd;
  color: #495057;
  width: 100%;
}

::v-deep .gr-qty-input:disabled {
  background-color: #e9ecef !important;
  color: #6c757d !important;
}

.gr-lot-input {
  height: 38px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #fff;
  color: #495057;
  width: 100%;
  word-break: break-word;
}

::v-deep .gr-lot-input:disabled {
  background-color: #ebebeb !important;
  color: #6c757d !important;
}

::v-deep .gr-lot-input:disabled::placeholder {
  color: #c6c6c6 !important;
}

/* ── Bonus section ── */
.gr-bonus-header {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
  padding: 16px 0 12px;
}

.gr-bonus-table-outer {
  margin-bottom: 16px;
}

.gr-bonus-select-wrap {
  flex: 1;
  min-width: 0;
}

.gr-bonus-select {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: 38px;
  background-color: #ebf3fd;
  background-image: none !important;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #212529;
  padding: 0 36px 0 14px;
}

::v-deep .gr-bonus-select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.gr-unit-select {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: 38px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #212529;
  padding: 0 36px 0 14px;
  background-color: #fff;
  background-image: none !important;
}

.gr-required-select {
  background-color: #ebf3fd !important;
}

::v-deep .gr-unit-select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

/* ── Delete button ── */
.gr-delete-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

.gr-delete-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border: 1px solid #ebebeb;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  padding: 0;
}

.gr-delete-btn:hover:not(:disabled) path {
  fill: #d33;
}

.gr-delete-btn:disabled {
  cursor: not-allowed;
}

::v-deep .form-control:disabled {
  opacity: 1;
}

/* ── Add bonus button ── */
.gr-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border: 1px solid #0d6efd;
  border-radius: 6px;
  background: #fff;
  color: #0d6efd;
  font-size: 15px;
  cursor: pointer;
}

.gr-add-plus {
  font-size: 18px;
  line-height: 1;
  font-weight: 400;
}

.gr-add-btn:hover {
  background-color: #f0f6ff;
}

/* Footer buttons */
.btn-confirm {
  padding: 8px 32px;
  font-size: 15px;
}

.btn-delete {
  padding: 8px 24px;
  font-size: 15px;
}

/* ── Responsive: Tablet (768px – 1399px) ── */
@media (max-width: 1399px) {
  ::v-deep .gr-select-po-modal .modal-dialog {
    max-width: 96vw;
    width: 96vw;
  }
}

@media (max-width: 1199px) {
  ::v-deep .gr-select-po-modal .modal-dialog {
    max-width: 98vw;
    width: 98vw;
  }

  ::v-deep .gr-select-po-modal .modal-header {
    padding: 16px 20px;
  }

  .gr-form-section {
    padding: 16px 20px;
  }

  .gr-table-section {
    padding: 16px 20px 20px;
  }

  .gr-label {
    width: 160px;
    font-size: 14px;
  }

  .gr-info-text {
    font-size: 14px;
  }

  .col-received {
    width: 120px;
  }

  .col-remaining {
    width: 90px;
  }

  .col-qty {
    width: 200px;
  }

  .col-unit {
    width: 110px;
  }

  .col-lot {
    width: 110px;
  }

  .col-expire {
    width: 148px;
  }
}

/* Tablet portrait / large phone (768px – 991px) */
@media (max-width: 991px) {
  ::v-deep .gr-select-po-modal .modal-dialog {
    max-width: 100vw;
    width: 100vw;
    margin: 0;
  }

  ::v-deep .gr-select-po-modal .modal-content {
    border-radius: 0;
  }

  ::v-deep .gr-select-po-modal .modal-header {
    padding: 14px 16px;
  }

  .gr-form-section {
    padding: 12px 16px;
  }

  .gr-form-row {
    gap: 8px;
    min-height: 44px;
  }

  .gr-label {
    width: 130px;
    font-size: 13px;
  }

  .gr-table-section {
    padding: 12px 16px 16px;
  }

  .gr-table thead th,
  .gr-table tbody td {
    padding: 6px;
    font-size: 13px;
  }

  .gr-qty-input,
  .gr-lot-input,
  .gr-select,
  .gr-bonus-select,
  .gr-unit-select {
    font-size: 13px;
  }

  .col-received {
    width: 100px;
  }

  .col-remaining {
    width: 80px;
  }

  .col-qty {
    width: 200px;
  }

  .col-unit {
    width: 90px;
  }

  .col-lot {
    width: 100px;
  }

  .col-expire {
    width: 136px;
  }

  .col-action {
    width: 48px;
  }

  .gr-delete-btn {
    width: 34px;
    height: 34px;
  }

  .gr-bonus-header {
    font-size: 14px;
  }

  .btn-confirm {
    padding: 7px 24px;
    font-size: 14px;
  }
}

::v-deep .multiselect--disabled {
  opacity: 1 !important;
}

.label-description {
  font-size: 14px;
  font-weight: 400;
  color: #6d747d;
}
</style>
